/**
 * O que os riscos do cargo dizem sobre o Código GFIP e o Enquadramento (#302).
 *
 * Quem calcula é o servidor; aqui só se pergunta e se traduz a resposta nos estados que a tela
 * mostra. A leitura de riscos não é da porta do cadastro de funcionário, então a pergunta passa
 * por `useDadoPermitido`: perfil sem `risk_application.read` vira "sem acesso", e não erro.
 */

import { useCallback } from 'react';

import { useDadoPermitido, type DadoGuardado } from '../authorization/useListaPermitida';
import {
  buscarAposentadoriaEspecial,
  type AposentadoriaEspecialDoCargo,
  type IdDoBanco,
} from '../services/efetivosService';
import { ApiRequestError } from '../types/common';

export type EstadoDoCalculo =
  | { tipo: 'sem-cargo' }
  | { tipo: 'calculando' }
  | { tipo: 'sem-acesso' }
  | { tipo: 'falha' }
  | { tipo: 'resposta'; resposta: AposentadoriaEspecialDoCargo };

/** A resposta guarda de qual cargo veio: a do cargo anterior nunca vale para o atual. */
interface RespostaDoCargo {
  cargoId: string;
  resposta: AposentadoriaEspecialDoCargo | null;
  semAcesso: boolean;
  /** O servidor falhou para este cargo: o erro não vale para o próximo. */
  falhou?: boolean;
}

export function useAposentadoriaEspecialDoCargo(cargoId: IdDoBanco | ''): {
  estado: EstadoDoCalculo;
  tentarDeNovo: () => void;
} {
  const temCargo = cargoId !== '';

  const carregar = useCallback(async (): Promise<RespostaDoCargo> => {
    if (cargoId === '') return { cargoId: '', resposta: null, semAcesso: false };
    try {
      return {
        cargoId: String(cargoId),
        resposta: await buscarAposentadoriaEspecial(cargoId),
        semAcesso: false,
      };
    } catch (falha: unknown) {
      // O servidor recusar é um estado da tela, não uma falha de rede.
      if (falha instanceof ApiRequestError && falha.status === 403) {
        return { cargoId: String(cargoId), resposta: null, semAcesso: true };
      }
      return { cargoId: String(cargoId), resposta: null, semAcesso: false, falhou: true };
    }
  }, [cargoId]);

  const guardado = useDadoPermitido('risk_application.read', 'Riscos do cargo', carregar, {
    habilitado: temCargo,
  });

  return { estado: estadoDe(temCargo, cargoId, guardado), tentarDeNovo: guardado.recarregar };
}

function estadoDe(
  temCargo: boolean,
  cargoId: IdDoBanco | '',
  guardado: DadoGuardado<RespostaDoCargo>
): EstadoDoCalculo {
  if (!temCargo) return { tipo: 'sem-cargo' };
  if (!guardado.resolvida) return { tipo: 'calculando' };
  if (!guardado.permitido) return { tipo: 'sem-acesso' };
  const dado = guardado.dado;
  if (!dado || dado.cargoId !== String(cargoId)) return { tipo: 'calculando' };
  if (dado.falhou) return { tipo: 'falha' };
  if (dado.semAcesso || !dado.resposta) return { tipo: 'sem-acesso' };
  return { tipo: 'resposta', resposta: dado.resposta };
}
